﻿import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  api,
  type DispatchGenerateResult,
  type DocumentTemplateRow,
  type GeneratedDispatchRow,
  type OwnerVoteAccessRow
} from "../../app/api";
import { isActionMenuEventTarget, useActionMenuAutoClose, useFloatingActionMenu } from "../../hooks/useActionMenuAutoClose";

type DispatchEventType = "einladung_versendet" | "erinnerung_1_versendet" | "erinnerung_2_versendet" | "beschlussergebnis_versendet";

type ShippingProcessPanelProps = {
  resolutionId: string;
  hasDigitalRecipients: boolean;
  hasAnalogRecipients: boolean;
  digitalRecipientCount: number;
  analogRecipientCount: number;
};

const formatDateTimeDE = (value?: string) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(d);
};

const eventLabel = (eventType: DispatchEventType) => {
  if (eventType === "einladung_versendet") return "Einladung";
  if (eventType === "erinnerung_1_versendet") return "1. Erinnerung";
  if (eventType === "erinnerung_2_versendet") return "2. Erinnerung";
  return "Beschlussergebnis";
};

const generatedDocumentLabel = (row: GeneratedDispatchRow) => {
  if (row.templateKey === "ballot_analog") return "Abstimmformular";
  if (row.templateKey === "invitation_digital" || row.templateKey === "invitation_analog") return "Einladung";
  if (row.templateKey === "reminder1_digital" || row.templateKey === "reminder1_analog") return "1. Erinnerung";
  if (row.templateKey === "reminder2_digital" || row.templateKey === "reminder2_analog") return "2. Erinnerung";
  if (row.templateKey === "result_digital" || row.templateKey === "result_analog") return "Beschlussergebnis";
  return eventLabel(row.eventType);
};

const templateEventLabel = (eventType: DocumentTemplateRow["eventType"]) => {
  if (eventType === "einladung_versendet") return "Einladung";
  if (eventType === "erinnerung_1_versendet") return "1. Erinnerung";
  if (eventType === "erinnerung_2_versendet") return "2. Erinnerung";
  if (eventType === "resolution_started") return "Beschluss gestartet";
  if (eventType === "invitation_sent_success") return "Einladung erfolgreich versendet";
  if (eventType === "email_bounce_after_retries") return "E-Mail-Rückläufer nach Retries";
  if (eventType === "analog_postal_action_required") return "Analoger Versand erforderlich";
  if (eventType === "vote_with_comment") return "Stimmabgabe mit Kommentar";
  if (eventType === "reminder_sent") return "Erinnerung versendet";
  if (eventType === "each_vote_submitted") return "Jede Stimmabgabe";
  if (eventType === "preliminary_result_locked") return "Vorläufiges Endergebnis erreicht";
  if (eventType === "resolution_finished_with_result") return "Beschluss beendet";
  return "Beschlussergebnis";
};

const templateDisplayName = (row: DocumentTemplateRow) => {
  if (row.key === "ballot_analog") return "Abstimmformular analog";
  return row.name;
};

const isTriggerTemplateEvent = (eventType: DocumentTemplateRow["eventType"]) => {
  return (
    eventType === "resolution_started" ||
    eventType === "invitation_sent_success" ||
    eventType === "email_bounce_after_retries" ||
    eventType === "analog_postal_action_required" ||
    eventType === "vote_with_comment" ||
    eventType === "reminder_sent" ||
    eventType === "each_vote_submitted" ||
    eventType === "preliminary_result_locked" ||
    eventType === "resolution_finished_with_result"
  );
};

const isPdfFileName = (fileName?: string) => typeof fileName === "string" && fileName.trim().toLowerCase().endsWith(".pdf");

const digitalMailStatusLabel = (status?: GeneratedDispatchRow["digitalMailStatus"]) => {
  if (status === "sent") return "Versendet";
  if (status === "failed") return "Fehlgeschlagen";
  if (status === "skipped") return "Übersprungen";
  return "-";
};

const digitalMailStatusBadge = (status?: GeneratedDispatchRow["digitalMailStatus"]) => {
  if (status === "sent") return "aktiv";
  if (status === "failed") return "ergebnis-abgelehnt";
  if (status === "skipped") return "vote-enthaltung";
  return "inaktiv";
};

const ShippingProcessTableIcon = ({ name }: { name: "document" | "mail" | "type" | "date" | "owner" | "status" | "size" | "action" | "key" }) => {
  if (name === "mail") {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <rect x="3.8" y="5.2" width="12.4" height="9.6" rx="1.6" stroke="currentColor" strokeWidth="1.7" />
        <path d="m4.5 6 5.5 4.1L15.5 6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (name === "date") {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <rect x="4.1" y="5.2" width="11.8" height="10.2" rx="1.7" stroke="currentColor" strokeWidth="1.7" />
        <path d="M7.2 3.8v3M12.8 3.8v3M4.8 8.5h10.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === "owner") {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <circle cx="10" cy="7" r="2.8" stroke="currentColor" strokeWidth="1.7" />
        <path d="M4.8 16c.8-2.8 2.7-4.1 5.2-4.1s4.4 1.3 5.2 4.1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === "status") {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <circle cx="10" cy="10" r="5.9" stroke="currentColor" strokeWidth="1.7" />
        <path d="m10 7.2 1.8 2.5L10 12.8 8.2 9.7 10 7.2Z" fill="currentColor" />
      </svg>
    );
  }
  if (name === "size") {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M4.5 14.5h11M5.2 11.2h9.6M6 7.9h8M6.8 4.6h6.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === "action") {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <circle cx="10" cy="4.8" r="1.3" fill="currentColor" />
        <circle cx="10" cy="10" r="1.3" fill="currentColor" />
        <circle cx="10" cy="15.2" r="1.3" fill="currentColor" />
      </svg>
    );
  }
  if (name === "key") {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <circle cx="7.2" cy="10.1" r="3.1" stroke="currentColor" strokeWidth="1.7" />
        <path d="M10.1 10.1h6M14 10.1v2M16 10.1v2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === "type") {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M5.1 4.2h9.8v11.6H5.1z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
        <path d="M7.4 7.3h5.2M7.4 10h5.2M7.4 12.7h3.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M5.2 3.8h6.5l2.9 2.9v8.6a1.2 1.2 0 0 1-1.2 1.2H5.2A1.2 1.2 0 0 1 4 15.3V5a1.2 1.2 0 0 1 1.2-1.2Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M6.8 9.4h6.1M6.8 12.2h5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
};

const ShippingProcessTableHeader = ({ label, icon, center = false }: { label: string; icon: Parameters<typeof ShippingProcessTableIcon>[0]["name"]; center?: boolean }) => (
  <span className={`resolutions-v2-th-plain${center ? " resolution-detail-v2-th-center" : ""}`}>
    <span className="resolutions-v2-th-icon" aria-hidden="true"><ShippingProcessTableIcon name={icon} /></span>
    <span className="resolutions-v2-th-label">{label}</span>
  </span>
);

const resolveEnhancedMailStatus = (row: GeneratedDispatchRow) => {
  const failureClass = row.digitalMailNdrFailureClass;
  const enhancedCode = row.digitalMailNdrEnhancedStatusCode;
  const smtpCode = row.digitalMailNdrSmtpStatusCode;
  if (row.digitalMailStatus === "failed" && failureClass === "permanent") {
    const code = enhancedCode || smtpCode;
    return {
      badgeClass: "ergebnis-abgelehnt",
      label: code ? `Permanent (${code})` : "Permanent (NDR)",
      hint: row.digitalMailNdrReason || row.digitalMailError || "Unzustellbar laut NDR (5.x.x)."
    };
  }
  if (row.digitalMailStatus === "failed" && failureClass === "temporary") {
    const code = enhancedCode || smtpCode;
    return {
      badgeClass: "neutral",
      label: code ? `Temporär (${code})` : "Temporär",
      hint: row.digitalMailNdrReason || row.digitalMailError || "Temporärer Zustellfehler laut NDR (4.x.x)."
    };
  }

  return {
    badgeClass: digitalMailStatusBadge(row.digitalMailStatus),
    label: digitalMailStatusLabel(row.digitalMailStatus),
    hint: row.digitalMailError || ""
  };
};

const DIGITAL_MAIL_MAX_ATTEMPTS = 3;
const normalizeEmail = (value?: string) => String(value ?? "").trim().toLowerCase();
const isValidEmailLike = (value?: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value ?? "").trim());
const getDispatchRowActivityAt = (row: GeneratedDispatchRow) =>
  row.digitalMailSentAt || row.digitalMailAttemptedAt || row.digitalMailNdrDetectedAt || row.createdAt || "";
const getDispatchRowDisplayTimestamp = (row: GeneratedDispatchRow) =>
  row.kanal === "digital" && row.templateKey !== "ballot_analog"
    ? row.digitalMailSentAt || row.digitalMailAttemptedAt || row.createdAt
    : row.createdAt;

export const ShippingProcessPanel = ({
  resolutionId,
  hasDigitalRecipients,
  hasAnalogRecipients,
  digitalRecipientCount,
  analogRecipientCount
}: ShippingProcessPanelProps) => {
  const qc = useQueryClient();
  const [generateStatus, setGenerateStatus] = useState<{ type: "ok" | "error"; text: string } | null>(null);
  const [showShippingTemplates, setShowShippingTemplates] = useState(false);
  const [showTriggerTemplates, setShowTriggerTemplates] = useState(false);
  const [previewRow, setPreviewRow] = useState<GeneratedDispatchRow | null>(null);
  const [replaceAddressRow, setReplaceAddressRow] = useState<GeneratedDispatchRow | null>(null);
  const [previewTestRecipientEmail, setPreviewTestRecipientEmail] = useState("");
  const [previewTestMailStatus, setPreviewTestMailStatus] = useState<{ type: "ok" | "error"; text: string } | null>(null);
  const [openActionMenuId, setOpenActionMenuId] = useState<string | null>(null);
  const [isActionMenuHovered, setIsActionMenuHovered] = useState(false);
  const { actionMenuPosition, openActionMenuAtTrigger } = useFloatingActionMenu({
    openMenuId: openActionMenuId,
    setOpenMenuId: setOpenActionMenuId,
    menuWidth: 190
  });

  useEffect(() => {
    if (!openActionMenuId) return;
    const closeMenu = (event: MouseEvent) => {
      if (isActionMenuEventTarget(event.target)) return;
      setOpenActionMenuId(null);
    };
    document.addEventListener("mousedown", closeMenu);
    return () => document.removeEventListener("mousedown", closeMenu);
  }, [openActionMenuId]);

  useEffect(() => {
    if (!openActionMenuId) setIsActionMenuHovered(false);
  }, [openActionMenuId]);

  useActionMenuAutoClose({
    isOpen: !!openActionMenuId,
    isHovered: isActionMenuHovered,
    closeMenu: () => setOpenActionMenuId(null)
  });

  const templatesQuery = useQuery({
    queryKey: ["document-templates"],
    queryFn: () => api.documentTemplates(),
    enabled: Boolean(resolutionId)
  });

  const resolutionQuery = useQuery({
    queryKey: ["resolution", resolutionId],
    queryFn: () => api.resolutionById(resolutionId),
    enabled: Boolean(resolutionId)
  });

  const generatedDispatchQuery = useQuery({
    queryKey: ["generated-dispatch", resolutionId],
    queryFn: () => api.generatedDispatchDocuments(resolutionId),
    enabled: Boolean(resolutionId),
    staleTime: 0,
    refetchOnMount: "always",
    refetchInterval: () => (typeof document !== "undefined" && document.visibilityState === "visible" ? 60_000 : false),
    refetchOnWindowFocus: false
  });

  const ownerAccessesQuery = useQuery({
    queryKey: ["resolution-owner-accesses", resolutionId],
    queryFn: () => api.resolutionOwnerAccesses(resolutionId),
    enabled: Boolean(resolutionId),
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false
  });

  const generateDispatchMutation = useMutation({
    mutationFn: async (payload: { eventType: DispatchEventType; sendDigital: boolean }) =>
      api.generateDispatchDocuments({ resolutionId, eventType: payload.eventType, sendDigital: payload.sendDigital }),
    onSuccess: async (result: DispatchGenerateResult, payload) => {
      await qc.invalidateQueries({ queryKey: ["generated-dispatch", resolutionId] });
      await qc.invalidateQueries({ queryKey: ["resolution-dispatch-log", resolutionId] });
      if (!payload.sendDigital) {
        setGenerateStatus({ type: "ok", text: "Dokumente erzeugt." });
        return;
      }
      if (result.digitalMail) {
        if (result.digitalMail.failed > 0) {
          setGenerateStatus({
            type: "error",
            text: `Dokumente erzeugt. Digitaler Versand: ${result.digitalMail.sent} versendet, ${result.digitalMail.failed} fehlgeschlagen.`
          });
        } else if (result.digitalMail.total > 0) {
          setGenerateStatus({
            type: "ok",
            text: `Dokumente erzeugt. Digitaler Versand erfolgreich (${result.digitalMail.sent} E-Mails).`
          });
        } else {
          setGenerateStatus({ type: "ok", text: "Dokumente erzeugt." });
        }
      } else {
        setGenerateStatus({ type: "ok", text: "Dokumente erzeugt." });
      }
    }
  });

  const updateDispatchAutomationMutation = useMutation({
    mutationFn: async (enabled: boolean) =>
      api.updateResolution(resolutionId, { dispatch_automation_enabled: enabled }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["resolution", resolutionId] });
      await qc.invalidateQueries({ queryKey: ["resolutions"] });
    },
    onError: (error) => {
      setGenerateStatus({
        type: "error",
        text: error instanceof Error ? error.message : "Automatik-Einstellung konnte nicht gespeichert werden."
      });
    }
  });

  const retryGeneratedMailMutation = useMutation({
    mutationFn: async (payload: { rowId: string; eventType: DispatchEventType; useCurrentOwnerEmail?: boolean }) =>
      api.retryGeneratedDispatchMail(payload.rowId, {
        resolutionId,
        eventType: payload.eventType,
        useCurrentOwnerEmail: payload.useCurrentOwnerEmail
      }),
    onSuccess: async (result) => {
      await qc.invalidateQueries({ queryKey: ["generated-dispatch", resolutionId] });
      setReplaceAddressRow(null);
      setGenerateStatus({
        type: result.digitalMailStatus === "sent" ? "ok" : "error",
        text:
          result.digitalMailStatus === "sent"
            ? result.usedCurrentOwnerEmail && result.replacedDigitalMailTo
              ? `Adresse für diesen Einzelversand von ${result.replacedDigitalMailTo} auf ${result.digitalMailTo ?? "aktuelle Adresse"} ersetzt und Mail versendet.`
              : `Mail an ${result.digitalMailTo ?? "Empfänger"} versendet.`
            : result.digitalMailError || "Mail konnte nicht versendet werden."
      });
    },
    onError: (error) => {
      setGenerateStatus({
        type: "error",
        text: error instanceof Error ? error.message : "Erneuter Mailversand fehlgeschlagen."
      });
    }
  });

  const mailPreviewMutation = useMutation({
    mutationFn: async (row: GeneratedDispatchRow) =>
      api.previewDocumentTemplateMail({
        templateKey: row.templateKey,
        resolutionId,
        ownerId: row.ownerId,
        eventType: row.eventType
      }),
    onError: (error) => {
      setGenerateStatus({
        type: "error",
        text: error instanceof Error ? error.message : "Mail-Vorschau konnte nicht geladen werden."
      });
    }
  });
  const sendPreviewTestMailMutation = useMutation({
    mutationFn: async () => {
      if (!previewRow) throw new Error("Keine Mail-Vorschau ausgewählt.");
      const recipientEmail = previewTestRecipientEmail.trim();
      if (!recipientEmail) throw new Error("Bitte Empfänger-E-Mail für die Testmail eingeben.");
      return api.sendDocumentTemplatePreviewMailTest({
        templateKey: previewRow.templateKey,
        resolutionId,
        ownerId: previewRow.ownerId,
        eventType: previewRow.eventType,
        recipientEmail
      });
    },
    onSuccess: (result) => {
      setPreviewTestMailStatus({
        type: "ok",
        text: `Testmail an ${result.recipientEmail} versendet${result.messageId ? ` (Message-ID: ${result.messageId})` : ""}.`
      });
    },
    onError: (error) => {
      setPreviewTestMailStatus({
        type: "error",
        text: error instanceof Error ? error.message : "Testmail konnte nicht versendet werden."
      });
    }
  });

  const templateRows: DocumentTemplateRow[] = templatesQuery.data?.templates ?? [];
  const generatedRows: GeneratedDispatchRow[] = generatedDispatchQuery.data?.data ?? [];
  const ownerAccessRows: OwnerVoteAccessRow[] = ownerAccessesQuery.data?.data ?? [];
  const digitalGeneratedRows = generatedRows.filter((row) => row.kanal === "digital" && row.templateKey !== "ballot_analog");
  const analogGeneratedRows = generatedRows.filter((row) => row.kanal === "analog");
  const ownerLabelById = new Map(ownerAccessRows.map((row) => [row.ownerId, row.eigentuemername] as const));
  const ownerEmailById = new Map(ownerAccessRows.map((row) => [row.ownerId, row.email] as const));
  const digitalMailSummary = {
    total: digitalGeneratedRows.length,
    sent: digitalGeneratedRows.filter((row) => row.digitalMailStatus === "sent").length,
    failed: digitalGeneratedRows.filter((row) => row.digitalMailStatus === "failed").length,
    permanentFailed: digitalGeneratedRows.filter(
      (row) => row.digitalMailStatus === "failed" && row.digitalMailNdrFailureClass === "permanent"
    ).length,
    skipped: digitalGeneratedRows.filter((row) => row.digitalMailStatus === "skipped").length,
    pendingRetry: digitalGeneratedRows.filter((row) => row.digitalMailStatus === "failed" && (row.digitalMailRetries ?? 0) < DIGITAL_MAIL_MAX_ATTEMPTS).length,
    exhausted: digitalGeneratedRows.filter((row) => row.digitalMailStatus === "failed" && (row.digitalMailRetries ?? 0) >= DIGITAL_MAIL_MAX_ATTEMPTS).length,
    nextRetryAt:
      digitalGeneratedRows
        .map((row) => row.digitalMailNextRetryAt)
        .filter((value): value is string => Boolean(value))
        .sort((a, b) => a.localeCompare(b))[0] ?? null
  };

  const canGenerate = hasDigitalRecipients || hasAnalogRecipients;
  const channelLabel = hasDigitalRecipients && hasAnalogRecipients ? "digital + analog" : hasDigitalRecipients ? "digital" : hasAnalogRecipients ? "analog" : "keine Empfänger";
  const dispatchAutomationEnabled = Boolean(resolutionQuery.data?.dispatch_automation_enabled ?? false);

  const shippingTemplates = useMemo(
    () =>
      templateRows.filter((row) => {
        if (isTriggerTemplateEvent(row.eventType)) return false;
        if (row.kanal === "digital") return hasDigitalRecipients;
        return hasAnalogRecipients;
      }),
    [templateRows, hasDigitalRecipients, hasAnalogRecipients]
  );
  const selectedTriggerEvents = useMemo(() => {
    const configured = resolutionQuery.data?.trigger_notifications_events;
    if (!configured || typeof configured !== "object") return null;
    return new Set(
      Object.entries(configured)
        .filter(([, enabled]) => Boolean(enabled))
        .map(([eventType]) => eventType)
    );
  }, [resolutionQuery.data?.trigger_notifications_events]);
  const triggerTemplates = useMemo(
    () =>
      templateRows.filter((row) => {
        if (!isTriggerTemplateEvent(row.eventType)) return false;
        if (!selectedTriggerEvents) return true; // Fallback für Alt-Datensätze ohne Event-Konfiguration.
        return selectedTriggerEvents.has(row.eventType);
      }),
    [templateRows, selectedTriggerEvents]
  );
  const triggerChannel = resolutionQuery.data?.trigger_notifications_channel === "facilioo" ? "facilioo" : "email";
  const triggerChannelLabel = triggerChannel === "facilioo" ? "Facilioo" : "Email";

  const eventButtons: Array<{ eventType: DispatchEventType; generateTitle: string; sendTitle: string }> = [
    { eventType: "einladung_versendet", generateTitle: "Einladungspaket erzeugen", sendTitle: "Einladung versenden" },
    { eventType: "erinnerung_1_versendet", generateTitle: "1. Erinnerung erzeugen", sendTitle: "1. Erinnerung versenden" },
    { eventType: "erinnerung_2_versendet", generateTitle: "2. Erinnerung erzeugen", sendTitle: "2. Erinnerung versenden" },
    { eventType: "beschlussergebnis_versendet", generateTitle: "Ergebnispaket erzeugen", sendTitle: "Ergebnis versenden" }
  ];

  return (
    <>
      <section className="panel shipping-process-v2-main-panel">
        <h3 style={{ marginTop: 0 }}>Versandprozess</h3>
        <div className="panel-inset v2-info-banner shipping-process-automation-v2-info" style={{ marginTop: 10 }}>
          <div className="settings-switch-row" style={{ marginBottom: 6 }}>
            <label className="settings-toggle" title="Automatischer Versandprozess">
              <input
                type="checkbox"
                checked={dispatchAutomationEnabled}
                disabled={updateDispatchAutomationMutation.isPending || resolutionQuery.isLoading}
                onChange={(e) => {
                  setGenerateStatus(null);
                  updateDispatchAutomationMutation.mutate(Boolean(e.target.checked));
                }}
              />
              <span className="settings-toggle-slider" />
            </label>
            <div>
              <strong>Automatischer Versandprozess</strong>
              <small>
                {dispatchAutomationEnabled
                  ? "Automatik aktiv: Dokumente werden zum Termin erzeugt und E-Mails automatisch versendet."
                  : "Automatik aus: Dokumente werden zum Termin erzeugt, der Versand erfolgt nur nach manueller Bestätigung."}
              </small>
            </div>
          </div>
        </div>
        {!dispatchAutomationEnabled ? (
          <>
            <div className="panel-inset shipping-process-v2-template-panel" style={{ marginTop: 10, background: "#ffffff" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, gap: 8, flexWrap: "wrap" }}>
                <div style={{ fontWeight: 700 }}>Verwendete Vorlagen ({channelLabel})</div>
                <button type="button" style={{ marginLeft: "auto" }} onClick={() => setShowShippingTemplates((prev) => !prev)}>
                  {showShippingTemplates ? "Einklappen" : "Ausklappen"}
                </button>
              </div>
              {showShippingTemplates ? (
                <div className="resolution-detail-v2-table-wrap shipping-process-v2-table-wrap">
                  <table className="resolution-detail-v2-table resolution-detail-shipping-v2-table shipping-process-templates-v2-table">
                    <thead>
                      <tr>
                        <th><ShippingProcessTableHeader label="Vorlage" icon="document" /></th>
                        <th style={{ textAlign: "center" }}><ShippingProcessTableHeader label="Kanal" icon="mail" center /></th>
                        <th style={{ textAlign: "center" }}><ShippingProcessTableHeader label="Typ" icon="type" center /></th>
                      </tr>
                    </thead>
                    <tbody>
                      {shippingTemplates.map((row) => (
                        <tr key={row.key}>
                          <td>{templateDisplayName(row)}</td>
                          <td style={{ textAlign: "center" }}>
                            <span className="badge kanal-grau">{row.kanal}</span>
                          </td>
                          <td style={{ textAlign: "center" }}>{templateEventLabel(row.eventType)}</td>
                        </tr>
                      ))}
                      {!shippingTemplates.length ? <tr><td colSpan={3}>Keine passenden Versand-Vorlagen für die aktuelle Erreichbarkeit.</td></tr> : null}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
            <div className="panel-inset shipping-process-v2-template-panel" style={{ marginTop: 10, background: "#ffffff" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, gap: 8, flexWrap: "wrap" }}>
                <div style={{ fontWeight: 700 }}>Trigger-Vorlagen (interne Verwaltungsnachrichten)</div>
                <button type="button" style={{ marginLeft: "auto" }} onClick={() => setShowTriggerTemplates((prev) => !prev)}>
                  {showTriggerTemplates ? "Einklappen" : "Ausklappen"}
                </button>
              </div>
              {showTriggerTemplates ? (
                <div className="resolution-detail-v2-table-wrap shipping-process-v2-table-wrap">
                  <table className="resolution-detail-v2-table resolution-detail-shipping-v2-table shipping-process-templates-v2-table">
                    <thead>
                      <tr>
                        <th><ShippingProcessTableHeader label="Vorlage" icon="document" /></th>
                        <th style={{ textAlign: "center" }}><ShippingProcessTableHeader label="Kanal" icon="mail" center /></th>
                        <th style={{ textAlign: "center" }}><ShippingProcessTableHeader label="Typ" icon="type" center /></th>
                      </tr>
                    </thead>
                    <tbody>
                      {triggerTemplates.map((row) => (
                        <tr key={row.key}>
                          <td>{templateDisplayName(row)}</td>
                          <td style={{ textAlign: "center" }}>
                            <span className="badge kanal-grau">{triggerChannelLabel}</span>
                          </td>
                          <td style={{ textAlign: "center" }}>{templateEventLabel(row.eventType)}</td>
                        </tr>
                      ))}
                      {!triggerTemplates.length ? <tr><td colSpan={3}>Keine Trigger-Vorlagen vorhanden.</td></tr> : null}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          </>
        ) : null}
        {!dispatchAutomationEnabled ? (
          <div className="form-actions" style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 8 }}>
            {eventButtons.map((btn) => (
              <div key={btn.eventType} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <button
                  type="button"
                  disabled={!canGenerate || generateDispatchMutation.isPending}
                  onClick={() => {
                    setGenerateStatus(null);
                    generateDispatchMutation.mutate({ eventType: btn.eventType, sendDigital: false });
                  }}
                  title={canGenerate ? `Erzeugt ${eventLabel(btn.eventType)} für ${channelLabel} (ohne E-Mail-Versand)` : "Keine Empfänger vorhanden"}
                >
                  {generateDispatchMutation.isPending ? "Erzeuge..." : btn.generateTitle}
                </button>
                <button
                  type="button"
                  disabled={!canGenerate || generateDispatchMutation.isPending || !hasDigitalRecipients}
                  onClick={() => {
                    setGenerateStatus(null);
                    generateDispatchMutation.mutate({ eventType: btn.eventType, sendDigital: true });
                  }}
                  title={hasDigitalRecipients ? `Löst den digitalen Versand für ${eventLabel(btn.eventType)} aus` : "Keine digitalen Empfänger vorhanden"}
                >
                  {generateDispatchMutation.isPending ? "Versende..." : btn.sendTitle}
                </button>
              </div>
            ))}
          </div>
        ) : null}
        {generateStatus ? (
          <div style={{ marginTop: 10 }}>
            <small style={{ color: generateStatus.type === "ok" ? "#0f7b31" : "#b42318", fontWeight: 600 }}>{generateStatus.text}</small>
          </div>
        ) : null}
      </section>

      <section className="panel shipping-process-v2-main-panel">
        <h3 style={{ marginTop: 0 }}>Erzeugte Versanddokumente</h3>
        <div className="panel-inset" style={{ marginBottom: 10 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 8 }}>
            <div className="kpi">
              <span>Digital</span>
              <strong className="shipping-process-v2-kpi-lines">
                <span className="shipping-process-v2-kpi-line">{digitalRecipientCount} Zustelladressen</span>
                <span className="shipping-process-v2-kpi-line">{digitalMailSummary.sent} erfolgreich versendet</span>
              </strong>
            </div>
            <div className="kpi">
              <span>Analog</span>
              <strong className="shipping-process-v2-kpi-lines">
                <span className="shipping-process-v2-kpi-line">{analogRecipientCount} Empfänger</span>
                <span className="shipping-process-v2-kpi-line">{analogGeneratedRows.length} Dokumente erzeugt</span>
              </strong>
            </div>
            <div className={`kpi shipping-process-v2-delivery-kpi shipping-process-v2-delivery-kpi--${digitalMailSummary.failed > 0 ? "error" : "ok"}`}><span>Fehlgeschlagen</span><strong>{digitalMailSummary.failed}</strong></div>
            <div className={`kpi shipping-process-v2-delivery-kpi shipping-process-v2-delivery-kpi--${digitalMailSummary.permanentFailed > 0 ? "error" : "ok"}`}><span>Permanent (5.x.x)</span><strong>{digitalMailSummary.permanentFailed}</strong></div>
          </div>
        </div>
        <div className="resolution-detail-v2-table-wrap shipping-process-v2-table-wrap" style={{ maxHeight: 260 }}>
          <table className="resolution-detail-v2-table resolution-detail-shipping-v2-table shipping-process-generated-v2-table">
            <thead>
              <tr>
                <th><ShippingProcessTableHeader label="Zeitpunkt" icon="date" /></th>
                <th><ShippingProcessTableHeader label="Eigentümer" icon="owner" /></th>
                <th style={{ textAlign: "center" }}><ShippingProcessTableHeader label="Kanal" icon="mail" center /></th>
                <th style={{ textAlign: "center" }}><ShippingProcessTableHeader label="E-Mail" icon="mail" center /></th>
                <th style={{ textAlign: "center" }}><ShippingProcessTableHeader label="Mail-Status" icon="status" center /></th>
                <th style={{ textAlign: "center" }}><ShippingProcessTableHeader label="Versuche" icon="key" center /></th>
                <th style={{ textAlign: "center" }}><ShippingProcessTableHeader label="Nächster Retry" icon="date" center /></th>
                <th><ShippingProcessTableHeader label="Dokument" icon="document" /></th>
                <th className="resolutions-v2-actions-head shipping-process-v2-actions-head" aria-label="Aktionen" title="Aktionen">
                  <ShippingProcessTableHeader label="Aktionen" icon="action" center />
                </th>
              </tr>
            </thead>
            <tbody>
              {generatedRows.map((row) => {
                const menuId = `shipping-${row.id}`;
                const canOpenPdf = isPdfFileName(row.fileName);
                const canPreviewMail = row.kanal === "digital" && row.templateKey !== "ballot_analog";
                const currentOwnerEmail = ownerEmailById.get(row.ownerId);
                const hasCurrentOwnerEmailReplacement =
                  isValidEmailLike(currentOwnerEmail) &&
                  normalizeEmail(currentOwnerEmail) !== normalizeEmail(row.digitalMailTo);
                const rowActivityAt = getDispatchRowActivityAt(row);
                const hasLaterSuccessfulDeliveryProof = generatedRows.some((candidate) =>
                  candidate.id !== row.id &&
                  candidate.ownerId === row.ownerId &&
                  candidate.kanal === "digital" &&
                  candidate.templateKey !== "ballot_analog" &&
                  candidate.digitalMailStatus === "sent" &&
                  normalizeEmail(candidate.digitalMailTo) === normalizeEmail(row.digitalMailTo) &&
                  (!rowActivityAt || getDispatchRowActivityAt(candidate).localeCompare(rowActivityAt) >= 0)
                );
                const canSendMail =
                  canPreviewMail &&
                  (row.digitalMailNdrFailureClass !== "permanent" || hasCurrentOwnerEmailReplacement || hasLaterSuccessfulDeliveryProof);
                const hasActions = canOpenPdf || canPreviewMail || canSendMail;
                const sendMailLabel = hasCurrentOwnerEmailReplacement
                  ? "Mit aktueller Adresse senden"
                    : row.digitalMailStatus === "sent"
                    ? "Erneut versenden"
                    : row.digitalMailStatus === "failed"
                      ? "Mail erneut versenden"
                      : "Mail versenden";
                const actionMenuHeight = 12 + [canOpenPdf, canPreviewMail, canSendMail].filter(Boolean).length * 42;
                return (
                  <tr key={row.id}>
                    <td>{formatDateTimeDE(getDispatchRowDisplayTimestamp(row))}</td>
                    <td>{row.eigentuemerName || "-"}</td>
                    <td style={{ textAlign: "center" }}><span className="badge kanal-grau">{row.kanal}</span></td>
                    <td style={{ textAlign: "center" }}>{row.digitalMailTo || "-"}</td>
                    <td style={{ textAlign: "center" }}>
                      {(() => {
                        const statusInfo = resolveEnhancedMailStatus(row);
                        return (
                          <span className={`badge ${statusInfo.badgeClass}`} title={statusInfo.hint || undefined}>
                            {statusInfo.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {row.kanal === "digital" && row.templateKey !== "ballot_analog" ? `${row.digitalMailRetries ?? 0} / ${DIGITAL_MAIL_MAX_ATTEMPTS}` : "-"}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {row.digitalMailNextRetryAt ? formatDateTimeDE(row.digitalMailNextRetryAt) : "-"}
                    </td>
                    <td>{generatedDocumentLabel(row)}</td>
                    <td className="shipping-process-v2-actions-cell">
                      <div className="resolutions-v2-action-wrap" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="resolutions-v2-action-trigger"
                          aria-label={`Aktionen für ${generatedDocumentLabel(row)} von ${row.eigentuemerName || "Eigentümer"}`}
                          aria-expanded={openActionMenuId === menuId}
                          disabled={!hasActions}
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!hasActions) return;
                            if (openActionMenuId === menuId) {
                              setOpenActionMenuId(null);
                              return;
                            }
                            openActionMenuAtTrigger(menuId, e.currentTarget, actionMenuHeight);
                          }}
                        >
                          ⋮
                        </button>
                        {openActionMenuId === menuId && actionMenuPosition && typeof document !== "undefined" ? createPortal(
                          <div
                            className="resolutions-v2-action-menu resolutions-v2-action-menu--fixed"
                            style={{ left: actionMenuPosition.left, top: actionMenuPosition.top }}
                            onMouseDown={(e) => e.stopPropagation()}
                            onMouseEnter={() => setIsActionMenuHovered(true)}
                            onMouseLeave={() => setIsActionMenuHovered(false)}
                          >
                            {canOpenPdf ? (
                              <a href={row.fileUrl} target="_blank" rel="noreferrer" onClick={() => setOpenActionMenuId(null)}>
                                PDF öffnen
                              </a>
                            ) : null}
                            {canPreviewMail ? (
                              <button
                                type="button"
                                onClick={() => {
                                  setPreviewRow(row);
                                  setPreviewTestRecipientEmail(row.digitalMailTo ?? "");
                                  setPreviewTestMailStatus(null);
                                  mailPreviewMutation.mutate(row);
                                  setOpenActionMenuId(null);
                                }}
                                disabled={mailPreviewMutation.isPending}
                                title={`Vorschau für ${ownerLabelById.get(row.ownerId) ?? row.eigentuemerName}`}
                              >
                                {mailPreviewMutation.isPending && previewRow?.id === row.id ? "Lade..." : "Mail-Vorschau"}
                              </button>
                            ) : null}
                            {canSendMail ? (
                              <button
                                type="button"
                                disabled={retryGeneratedMailMutation.isPending}
                                onClick={() => {
                                  setGenerateStatus(null);
                                  if (hasCurrentOwnerEmailReplacement) {
                                    setReplaceAddressRow(row);
                                  } else {
                                    retryGeneratedMailMutation.mutate({ rowId: row.id, eventType: row.eventType });
                                  }
                                  setOpenActionMenuId(null);
                                }}
                                title={
                                  hasCurrentOwnerEmailReplacement
                                    ? `Aktuelle Adresse ${currentOwnerEmail} für diesen Einzelversand verwenden`
                                    : hasLaterSuccessfulDeliveryProof
                                      ? "Erneuter Versand möglich, da diese Adresse später erfolgreich zugestellt wurde."
                                    : row.digitalMailError || "E-Mail erneut versenden"
                                }
                              >
                                {retryGeneratedMailMutation.isPending ? "Sende..." : sendMailLabel}
                              </button>
                            ) : null}
                          </div>,
                          document.body
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!generatedRows.length ? <tr><td colSpan={9}>Noch keine erzeugten Versanddokumente.</td></tr> : null}
            </tbody>
          </table>
        </div>
      </section>
      {replaceAddressRow ? (
        <div className="modal-backdrop">
          <section className="panel modal-card shipping-process-address-confirm-modal">
            <h3 style={{ marginTop: 0, marginBottom: 8 }}>Aktuelle E-Mail-Adresse verwenden?</h3>
            <p>
              Für dieses Versanddokument ist <strong>{replaceAddressRow.digitalMailTo || "-"}</strong> gespeichert.
              In den Stammdaten steht aktuell <strong>{ownerEmailById.get(replaceAddressRow.ownerId) || "-"}</strong>.
            </p>
            <p>
              Soll die Adresse nur für diesen Einzelversand ersetzt und die Mail danach versendet werden?
            </p>
            <div className="form-actions" style={{ marginTop: 12 }}>
              <button
                type="button"
                className="resolution-primary-btn"
                disabled={retryGeneratedMailMutation.isPending}
                onClick={() => {
                  setGenerateStatus(null);
                  retryGeneratedMailMutation.mutate({
                    rowId: replaceAddressRow.id,
                    eventType: replaceAddressRow.eventType,
                    useCurrentOwnerEmail: true
                  });
                }}
              >
                {retryGeneratedMailMutation.isPending ? "Sende..." : "Mit aktueller Adresse senden"}
              </button>
              <button
                type="button"
                disabled={retryGeneratedMailMutation.isPending || replaceAddressRow.digitalMailNdrFailureClass === "permanent"}
                title={replaceAddressRow.digitalMailNdrFailureClass === "permanent" ? "Bei permanentem Zustellfehler nur mit aktueller Adresse möglich." : undefined}
                onClick={() => {
                  setGenerateStatus(null);
                  retryGeneratedMailMutation.mutate({ rowId: replaceAddressRow.id, eventType: replaceAddressRow.eventType });
                }}
              >
                Mit gespeicherter Adresse senden
              </button>
              <button type="button" onClick={() => setReplaceAddressRow(null)} disabled={retryGeneratedMailMutation.isPending}>
                Abbrechen
              </button>
            </div>
          </section>
        </div>
      ) : null}
      {previewRow ? (
        <div className="modal-backdrop">
          <section className="panel modal-card" style={{ width: "min(1400px, 98vw)", maxWidth: 1400, maxHeight: "94vh", overflow: "auto" }}>
            <h3 style={{ marginTop: 0, marginBottom: 8 }}>
              Mail-Vorschau: {ownerLabelById.get(previewRow.ownerId) ?? previewRow.eigentuemerName}
            </h3>
            <small style={{ color: "var(--text-soft)" }}>
              Beschluss {previewRow.beschlussnummer} | Objekt {previewRow.objektnummer} | Vorlage {previewRow.templateKey}
            </small>
            <div style={{ marginTop: 10 }}>
              {mailPreviewMutation.isPending ? (
                <div>Lade Vorschau...</div>
              ) : mailPreviewMutation.data ? (
                <iframe
                  title={`Mail-Vorschau ${previewRow.id}`}
                  srcDoc={mailPreviewMutation.data.html}
                  style={{ width: "100%", height: "80vh", border: "1px solid var(--line)", borderRadius: 6, background: "#fff" }}
                />
              ) : (
                <div className="error-text">Vorschau konnte nicht geladen werden.</div>
              )}
            </div>
            <section className="panel-inset" style={{ marginTop: 12 }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Testmail senden</div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <input
                  type="email"
                  className="table-field"
                  value={previewTestRecipientEmail}
                  onChange={(e) => setPreviewTestRecipientEmail(e.target.value)}
                  placeholder="empfänger@beispiel.de"
                  style={{ width: "min(280px, 100%)", flex: "0 0 auto" }}
                />
                <button
                  type="button"
                  onClick={() => {
                    setPreviewTestMailStatus(null);
                    sendPreviewTestMailMutation.mutate();
                  }}
                  disabled={sendPreviewTestMailMutation.isPending || !previewRow || !mailPreviewMutation.data}
                >
                  {sendPreviewTestMailMutation.isPending ? "Sende Testmail..." : "Testmail senden"}
                </button>
              </div>
              {previewTestMailStatus ? (
                <small style={{ display: "block", marginTop: 8, color: previewTestMailStatus.type === "ok" ? "#0f7b31" : "#b42318", fontWeight: 600 }}>
                  {previewTestMailStatus.text}
                </small>
              ) : null}
            </section>
            <div className="form-actions" style={{ marginTop: 10 }}>
              <button
                type="button"
                onClick={() => mailPreviewMutation.mutate(previewRow)}
                disabled={mailPreviewMutation.isPending}
              >
                {mailPreviewMutation.isPending ? "Aktualisiere..." : "Vorschau aktualisieren"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setPreviewRow(null);
                  setPreviewTestMailStatus(null);
                }}
              >
                Schließen
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </>
  );
};
